import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const Payments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const companyCode = queryParams.get('code') || location.state?.companyCode || '';
  const companyName = queryParams.get('name') || location.state?.companyName || 'Unknown Company';
  const companyType = queryParams.get('type') || location.state?.companyType || 'Unknown';
  const isPureVeg = queryParams.get('isPureVeg') || location.state?.isPureVeg || 'false';

  const [payments, setPayments] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedUnitCode, setSelectedUnitCode] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const filterType = queryParams.get('filterType') || 'year';

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const unitsQuery = query(collection(db, 'units'), where('companyCode', '==', companyCode));
        const unitsSnapshot = await getDocs(unitsQuery);
        const unitList = unitsSnapshot.docs.map(doc => ({
          unitCode: doc.id,
          name: doc.data().name || doc.id,
        }));
        setUnits(unitList);
        console.log('Payments: Fetched units:', unitList);
        if (unitList.length === 0) {
          console.warn('Payments: No units found for companyCode:', companyCode);
        }
      } catch (err) {
        console.error('Payments: Error fetching units:', err, { code: err.code, message: err.message });
        setError('Failed to load units.');
      }
    };

    const fetchPayments = async () => {
      if (!companyCode) {
        setError('Company code is missing.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');

        let paymentsQuery;
        if (selectedUnitCode === 'all') {
          paymentsQuery = query(
            collection(db, 'payments'),
            where('companyCode', '==', companyCode)
          );
        } else {
          paymentsQuery = query(
            collection(db, 'payments'),
            where('companyCode', '==', companyCode),
            where('unitCode', '==', selectedUnitCode)
          );
        }
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const paymentsData = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Apply date filters
const now = new Date();

const filteredPayments = paymentsData.filter(payment => {
  if (!payment.date?.toDate) return false;

  const paymentDate = payment.date.toDate();

  // TODAY
  if (filterType === "today") {
    return (
      paymentDate.getDate() === now.getDate() &&
      paymentDate.getMonth() === now.getMonth() &&
      paymentDate.getFullYear() === now.getFullYear()
    );
  }

  // MONTH
  if (filterType === "month") {
    return (
      paymentDate.getMonth() === now.getMonth() &&
      paymentDate.getFullYear() === now.getFullYear()
    );
  }

  // YEAR
  return paymentDate.getFullYear() === now.getFullYear();
});

setPayments(filteredPayments);
        //setPayments(paymentsData);

        console.log('Payments: Fetched data:', {
          companyCode,
          unitCode: selectedUnitCode,
          payments: paymentsData,
        });

        setIsLoading(false);
      } catch (error) {
        console.error('Payments: Error fetching payments:', error, { code: error.code, message: error.message });
        setError(`Failed to load payments: ${error.message}`);
        setIsLoading(false);
      }
    };

    if (companyCode) {
      fetchUnits();
      fetchPayments();
    }
  }, [companyCode, selectedUnitCode]);

  const handleTabChange = (event, newValue) => {
    setSelectedUnitCode(newValue);
    setIsLoading(true);
    setError('');
    console.log('Payments: Selected unitCode:', newValue);
  };

  const handleBackToDashboard = () => {
    const queryParams = new URLSearchParams({
      code: companyCode,
      type: companyType,
      name: companyName,
      isPureVeg: isPureVeg,
    });
    navigate(`/admin/home?${queryParams.toString()}`, {
      state: { companyCode, companyName, companyType, isPureVeg },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading payments...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToDashboard}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4, textAlign: 'center' }}
      >
        Payments ({filterType.toUpperCase()}) - {companyName} ({companyCode})
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={selectedUnitCode}
          onChange={handleTabChange}
          aria-label="unit filter tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Units" value="all" />
          {units.map(unit => (
            <Tab key={unit.unitCode} label={unit.name} value={unit.unitCode} />
          ))}
        </Tabs>
      </Box>

      <StyledCard>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Table ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Amount (₹)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length > 0 ? (
                payments.map((payment, index) => (
                  <TableRow
                    key={payment.id}
                    sx={{
                      bgcolor: index % 2 === 0 ? '#fafafa' : '#fff',
                      '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                  >
                    <TableCell>{payment.tableId || 'N/A'}</TableCell>
                    <TableCell>{payment.orderId || 'N/A'}</TableCell>
                    <TableCell>{units.find(u => u.unitCode === payment.unitCode)?.name || 'Unknown Unit'}</TableCell>
                    <TableCell>{payment.amount?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          payment.paymentMethod === 'Cash' ? 'success.main' :
                          payment.paymentMethod === 'Card' ? 'info.main' :
                          'text.primary'
                        }
                      >
                        {payment.paymentMethod || 'Online'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.date?.toDate().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }) || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {selectedUnitCode === 'all'
                        ? 'No payments in all units.'
                        : `No payments in the selected unit (${units.find(u => u.unitCode === selectedUnitCode)?.name || selectedUnitCode}).`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCard>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToDashboard}
          sx={{ minWidth: 150 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );
};

export default Payments;